import { z } from 'zod';
import { AgentProviderSpec } from './llm-providers.js';
/**
 * Every agent-service response uses this envelope. The Python service defines the same shape in
 * Pydantic (services/agents/app/envelope.py); `agent-envelope.schema.json` is exported from there and
 * checked against this schema in tests, so the two can't drift.
 *
 * Agents only propose. The core validates each proposal before anything is recorded.
 */
export const Evidence = z.object({
    kind: z.enum(['chat_message', 'document', 'integration', 'control_library']),
    ref: z.string(),
    page: z.number().int().positive().nullable().default(null),
    quote: z.string().nullable().default(null),
});
export const Proposal = z.object({
    target: z.string().min(1), // e.g. "question:BK-02"
    value: z.unknown(),
    confidence: z.number().min(0).max(1),
    evidence: z.array(Evidence).default([]),
});
export const Source = z.object({
    id: z.string(),
    title: z.string(),
    uri: z.string().nullable().default(null),
});
export const Usage = z.object({
    input_tokens: z.number().int().nonnegative(),
    output_tokens: z.number().int().nonnegative(),
    cost_usd: z.number().nonnegative(),
});
export const AgentEnvelope = z.object({
    proposals: z.array(Proposal),
    sources: z.array(Source),
    message: z.string().nullable().default(null),
    model: z.string(),
    prompt_version: z.string(),
    trace_id: z.string(),
    usage: Usage,
});
/** Request for POST /v1/chat/turn. Mirrors ChatTurnRequest in services/agents/app/agents/conversation.py. */
export const AgentQuestionType = z.enum([
    'yes_no',
    'single_choice',
    'multi_choice',
    'scale',
    'number',
    'date',
    'short_text',
]);
export const OpenQuestion = z.object({
    id: z.string(),
    text: z.string(),
    type: AgentQuestionType,
    options: z.array(z.object({ value: z.string(), label: z.string() })).default([]),
    kill_switch: z.boolean().default(false),
    min_confidence: z.number().min(0).max(1).default(0.75),
    /** The question's help, for explaining it, and its configured instruction to the assistant. */
    help: z.string().max(1000).default(''),
    instruction: z.string().max(1000).default(''),
});
/** An earlier line of the conversation, oldest first: context for a short follow-up reply. */
export const HistoryLine = z.strictObject({
    role: z.enum(['assistant', 'user']),
    text: z.string().min(1).max(1000),
});
// ---------- what the core tells the agents about the assessment (decision 0013) ----------
// Mirrors services/agents/app/agents/context.py.
export const AgentScoreAction = z.strictObject({ control: z.string(), name: z.string(), points: z.int() });
/** A mission (decision 0030): its points if done in full, and its next steps' points together. */
export const AgentMission = z.strictObject({
    id: z.string(),
    name: z.string(),
    points: z.int(),
    next_points: z.int(),
    steps: z.array(z.string()).max(3).default([]),
});
export const AgentChangePart = z.strictObject({ label: z.string(), points: z.int() });
export const AgentLastChange = z.strictObject({
    from_score: z.int(),
    to_score: z.int(),
    delta: z.int(),
    parts: z.array(AgentChangePart).max(5).default([]),
});
/** The score as the core explains it (task 24): the only score facts the explainer may state. */
export const AgentScoreContext = z.strictObject({
    score: z.int(),
    band: z.string(),
    provisional: z.boolean().default(false),
    cap: z.int().nullable().default(null),
    /** The kill-switch controls that cap the score. */
    cap_reasons: z.array(z.string()).max(12).default([]),
    top_actions: z.array(AgentScoreAction).max(5).default([]),
    top_missions: z.array(AgentMission).max(3).default([]),
    last_change: AgentLastChange.nullable().default(null),
});
export const ExplainContext = z.strictObject({
    /** The controls that apply to this assessment; others aren't retrieved. */
    controls: z.array(z.string()).max(300).default([]),
    /** The controls of the question being asked now. */
    focus: z.array(z.string()).max(20).default([]),
    score: AgentScoreContext.nullable().default(null),
});
export const ChatTurnRequest = z.strictObject({
    tenant_id: z.string(),
    assessment_id: z.string(),
    turn_id: z.string(),
    message: z.string().min(1),
    language: z.enum(['en', 'ms']).default('en'),
    /** The first question is the one being asked now; the rest are the next few the core would ask. */
    open_questions: z.array(OpenQuestion).min(1).max(10),
    /** The last few lines before this message, e.g. the clarifying question it answers. */
    history: z.array(HistoryLine).max(8).default([]),
    /** For questions about the assessment, which the explainer answers. */
    context: ExplainContext.default({ controls: [], focus: [], score: null }),
    /** The model to use, as configured by the platform. Absent: the agent service's own settings. */
    provider: AgentProviderSpec.nullable().optional(),
});
/** Request for POST /v1/chat/explain. Mirrors ExplainRequest in services/agents/app/agents/explainer.py. */
export const ExplainRequest = z.strictObject({
    tenant_id: z.string(),
    assessment_id: z.string(),
    turn_id: z.string(),
    message: z.string().min(1),
    language: z.enum(['en', 'ms']).default('en'),
    history: z.array(HistoryLine).max(8).default([]),
    current_question: OpenQuestion.nullable().default(null),
    context: ExplainContext.default({ controls: [], focus: [], score: null }),
    provider: AgentProviderSpec.nullable().optional(),
});
// ---------- phrasing (POST /v1/chat/phrase) ----------
/** A question to word for chat. Mirrors PhraseQuestion in services/agents/app/agents/phrasing.py. */
export const PhraseQuestion = OpenQuestion.omit({ min_confidence: true }).strict();
export const PhraseRequest = z.strictObject({
    tenant_id: z.string(),
    language: z.enum(['en', 'ms']).default('en'),
    questions: z.array(PhraseQuestion).min(1).max(20),
    provider: AgentProviderSpec.nullable().optional(),
});
export const QuestionPhrasing = z.object({
    id: z.string(),
    text: z.string(),
    why: z.string(),
    /** The quick replies with labels in the requested language; values exactly as configured. */
    options: z.array(z.object({ value: z.string(), label: z.string() })),
});
/** How the chat words each question. Kill-switch questions, and phrasings that fail a check, are refused. */
export const PhrasingEnvelope = z.object({
    phrasings: z.array(QuestionPhrasing),
    refused: z.array(z.object({ id: z.string(), reason: z.string() })),
    language: z.enum(['en', 'ms']),
    model: z.string(),
    prompt_version: z.string(),
    trace_id: z.string(),
    usage: Usage,
});
//# sourceMappingURL=agent-envelope.js.map